/**
 * The Measurements panel — `docs/property-maps-v2.md` §5.4, board 3.
 *
 * The editor's second panel tab, and the body of a phone sheet. Type chips
 * across the top, a group per measurement type, a row per measurement, and a
 * footer that says how many are waiting.
 *
 * **Saved and unsaved sit in one list, and the group header says both.**
 * `Hardscape · 640 sq ft` is what the property holds and what a quote prices
 * from; `+ 640 new` beside it in amber is what pressing *Save to property*
 * would add. Two lists would be the easy version and the one where somebody
 * saves eight of nine.
 *
 * **Nothing here saves on its own.** Every write goes through the review
 * dialog (§5.4: "the save is always reviewed"), because a measurement is a
 * number a quote is built on and the Overview card is where it lands.
 */
import { useMemo, useState } from 'react'
import { Button } from '../../components/primitives.js'
import { ContextMenu } from '../../components/context-menu.js'
import { cn } from '../../lib/cn.js'
import { formatNumber } from '../../lib/format.js'
import { IconMore, IconRuler } from '../../frame/icons.js'
import type { MeasurementGroup } from '../api.js'
import type { MeasureShape } from '../measure-session.js'
import { panelGroups, typeColour, type PanelRow } from './rows.js'

export function MeasurementsPanel({
  groups, tape, types, layerNames, canEdit, onSave, onRename, onRemove, onRefresh, onPick,
}: {
  groups: MeasurementGroup[]
  /** The measure tape, for the unsaved rows. Empty outside a session. */
  tape: MeasureShape[]
  /** The catalogue, so the first outline on a site has a group to land in. */
  types?: { measurementTypeId: string; name: string; unit: string }[]
  /** Layer names by feature id, so a mapped row can say which layer. */
  layerNames?: Record<string, string>
  canEdit: boolean
  /** **Save to property** — opens the review dialog with everything waiting. */
  onSave: () => void
  onRename: (row: PanelRow) => void
  onRemove: (row: PanelRow) => void
  /** **Update** on a *map changed* row. */
  onRefresh: (row: PanelRow) => void
  /**
   * Put the row's outline on screen and open its popup.
   *
   * One handler for both kinds, because from the panel they are the same
   * gesture — the editor is what knows that a saved row's outline is a
   * feature and an unsaved one's is on the tape.
   */
  onPick?: (row: PanelRow) => void
}) {
  const [only, setOnly] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ row: PanelRow; x: number; y: number } | null>(null)

  const all = useMemo(
    () => panelGroups(groups, tape, layerNames, types), [groups, tape, layerNames, types])
  const shown = only ? all.filter((group) => group.typeId === only) : all
  const waiting = all.reduce(
    (count, group) => count + group.rows.filter((row) => !row.saved).length, 0)

  if (all.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
        <span className="text-text-faint"><IconRuler size={20} /></span>
        <b className="text-cell">Nothing measured yet</b>
        <p className="m-0 max-w-[240px] text-sub text-text-muted">
          Pick Measure in the rail and outline an area. The numbers you add here land on the
          property.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* The type chips — All types, then the types that have rows (§5.4). */}
      {all.length > 1 && (
        <div className="flex flex-wrap gap-1.5 border-b border-line-faint px-3 py-2.5">
          <TypeChip label="All types" on={only === null} onClick={() => setOnly(null)} />
          {all.map((group) => (
            <TypeChip
              key={group.typeId}
              label={group.type}
              on={only === group.typeId}
              onClick={() => setOnly(group.typeId)}
            />
          ))}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {shown.map((group) => (
          <div key={group.typeId}>
            <div className="flex items-baseline gap-2 px-3 pb-1 pt-2">
              <span className="whitespace-nowrap text-label uppercase text-text-faint">
                {group.type}
                {group.saved !== null && ` · ${formatNumber(round(group.saved))} ${group.unit}`}
              </span>
              {group.waiting > 0 && (
                <span className="whitespace-nowrap text-label font-semibold text-warn">
                  {`+ ${formatNumber(round(group.waiting))} new`}
                </span>
              )}
            </div>

            {group.rows.map((row) => (
              <Row
                key={row.id}
                row={row}
                unit={group.unit}
                colour={typeColour(group.type)}
                canEdit={canEdit}
                onPick={onPick}
                onRefresh={onRefresh}
                onMenu={(at) => setMenu({ row, ...at })}
              />
            ))}
          </div>
        ))}
      </div>

      {/*
        * The footer is the only thing on this panel that writes, and it is
        * absent with nothing waiting rather than greyed: §5.4's save is
        * always reviewed, so a button with an empty dialog behind it is a
        * button that does nothing somebody can see.
        */}
      {waiting > 0 && (
        <div className={cn(
          'flex shrink-0 items-center gap-2 border-t border-line px-3 py-2.5 text-sub',
          'text-text-muted',
        )}
        >
          <span>{`${waiting} unsaved`}</span>
          <Button
            variant="primary"
            size="toolbar"
            className="ml-auto"
            disabled={!canEdit}
            onClick={onSave}
          >
            Save to property
          </Button>
        </div>
      )}

      {menu && (
        <ContextMenu
          x={menu.x}
          y={menu.y}
          onClose={() => setMenu(null)}
          items={[
            { key: 'rename', label: 'Rename…', onSelect: () => onRename(menu.row) },
            ...(menu.row.mapChanged
              ? [{
                key: 'refresh',
                label: 'Take the shape’s number',
                onSelect: () => onRefresh(menu.row),
              }]
              : []),
            {
              key: 'remove',
              label: menu.row.saved ? 'Remove from the property…' : 'Discard',
              tone: 'danger' as const,
              onSelect: () => onRemove(menu.row),
            },
          ]}
        />
      )}
    </div>
  )
}

/** Whole units for feet, two places for acres — `measurementValue`'s rule. */
const round = (value: number): number =>
  (Math.abs(value) >= 100 ? Math.round(value) : Number(value.toFixed(2)))

function TypeChip({ label, on, onClick }: {
  label: string; on: boolean; onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={cn(
        'inline-flex h-[22px] items-center rounded-chip px-2 text-chip font-medium',
        on ? 'bg-accent-soft text-accent-dark' : 'bg-field text-text-muted hover:bg-hover',
      )}
    >
      {label}
    </button>
  )
}

function Row({ row, unit, colour, canEdit, onPick, onRefresh, onMenu }: {
  row: PanelRow
  unit: string
  colour: string
  canEdit: boolean
  onPick?: (row: PanelRow) => void
  onRefresh: (row: PanelRow) => void
  onMenu: (at: { x: number; y: number }) => void
}) {
  return (
    <div
      className={cn(
        'grid items-center gap-2 border-b border-line-faint px-3 py-2 text-[12px]',
        'grid-cols-[10px_minmax(0,1fr)_92px_22px]',
        /* An unsaved row is tinted rather than tagged *only* — the tag says
           what it is and the tint says how many there are at a glance. */
        row.saved ? '' : 'bg-warn-bg/40',
      )}
    >
      <span
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 rounded-tag"
        style={{ background: colour }}
      />

      <div className="flex min-w-0 flex-col gap-px">
        <span className="flex min-w-0 items-center gap-1.5">
          {/* The name is the way to the outline — a row in a list about
              shapes that cannot show you the shape is half a row. */}
          <button
            type="button"
            disabled={!onPick || row.shapeDeleted}
            onClick={() => onPick?.(row)}
            className="truncate text-left font-semibold disabled:cursor-default"
          >
            {row.name || 'Measurement'}
          </button>
          {!row.saved && (
            <span className={cn(
              'inline-flex h-[19px] shrink-0 items-center rounded-tag bg-warn-bg px-1.5',
              'text-sub font-semibold text-warn',
            )}
            >
              unsaved
            </span>
          )}
        </span>
        <span className="truncate text-sub text-text-muted">
          {row.detail}
          {row.shapeDeleted && ' · shape deleted'}
        </span>
        {row.mapChanged && (
          <span className="flex items-center gap-1.5 text-sub text-warn">
            map changed
            <button
              type="button"
              disabled={!canEdit}
              onClick={() => onRefresh(row)}
              className="font-semibold underline underline-offset-2 disabled:opacity-50"
            >
              Update
            </button>
          </span>
        )}
      </div>

      <span className="whitespace-nowrap text-right tabular-nums">
        <b>{formatNumber(round(row.value))}</b>
        <span className="text-sub text-text-muted">{` ${unit}`}</span>
      </span>

      <span className="flex justify-end">
        <button
          type="button"
          aria-label={`More for ${row.name || 'this measurement'}`}
          onClick={(event) => {
            const box = event.currentTarget.getBoundingClientRect()
            onMenu({ x: box.right, y: box.bottom })
          }}
          className="text-text-faint hover:text-text"
        >
          <IconMore size={14} />
        </button>
      </span>
    </div>
  )
}
